// Make a plan: choose books from the canon, and if you like a book from the library to read alongside
// (F7: its sections spread over the same days after the Scripture, as the API spreads them), choose a
// pace (finish in N days, or N chapters a day), rest on the Lord's Day if you like. The days lay
// themselves out as you choose, beside a cover that draws itself: the plan's books light in gilt on
// the canon at its foot as they're chosen.
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Chip } from "../../../components/Badge";
import { Button, IconButton } from "../../../components/Button";
import { Field, Input, Select, Textarea } from "../../../components/Field";
import { PlanCover } from "../../../components/PlanCover";
import { Card } from "../../../components/Surface";
import { SegmentedControl } from "../../../components/Tabs";
import ui from "../../../components/ui.module.css";
import { useCreatePlan } from "../../../data/planQueries";
import { atNoon, buildSchedule, chapterCount, finishDate, localDate, readingsLine } from "../../../data/plans";
import { CHAPTERS } from "../../../data/study";
import { pageSections, sectionReadings, useCatalogue, useToc } from "../../../data/works";
import { useTitle } from "../../../lib/useTitle";
import s from "./new.module.css";

export const Route = createFileRoute("/_app/plans/new")({
  validateSearch: (q: Record<string, unknown>): { alongside?: string } =>
    typeof q.alongside === "string" && /^[a-z0-9-]{1,80}$/.test(q.alongside) ? { alongside: q.alongside } : {},
  component: Builder,
});

const QUICK: { label: string; books: string[] }[] = [
  { label: "A gospel", books: ["Mark"] },
  { label: "The Gospels", books: ["Matt", "Mark", "Luke", "John"] },
  { label: "Paul’s letters", books: ["Rom", "1Cor", "2Cor", "Gal", "Eph", "Phil", "Col", "1Thess", "2Thess", "1Tim", "2Tim", "Titus", "Phlm"] },
  { label: "Wisdom", books: ["Job", "Ps", "Prov", "Eccl", "Song"] },
  { label: "New Testament", books: CHAPTERS.slice(39).map(([id]) => id) },
];

const long = (iso: string) => atNoon(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const same = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

function Builder() {
  useTitle("Make a plan");
  const router = useRouter();
  const navigate = useNavigate();
  const create = useCreatePlan();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [books, setBooks] = useState<string[]>([]);
  const [pace, setPace] = useState<"days" | "chapters">("days");
  const [days, setDays] = useState(30);
  const [perDay, setPerDay] = useState(2);
  const [restSundays, setRestSundays] = useState(true);
  const [shared, setShared] = useState(true);
  const search = Route.useSearch();
  const { data: catalogue } = useCatalogue();
  const [workId, setWorkId] = useState(search.alongside ?? "");
  const [whole, setWhole] = useState<"whole" | "some">("whole");
  const [picked, setPicked] = useState<string[]>([]);
  const readable = useMemo(() => catalogue?.works.filter((w) => w.text) ?? [], [catalogue]);
  const work = readable.find((w) => w.id === workId) ?? null;
  const { data: toc } = useToc(work?.id);
  const pages = useMemo(() => (toc && work ? pageSections(toc) : []), [toc, work]);
  // The sections sent: every reader page of the work, or those picked. The API spreads them in contents order.
  const sectionUids = useMemo(
    () => (whole === "whole" ? pages.map((p) => p.uid) : pages.filter((p) => picked.includes(p.uid)).map((p) => p.uid)),
    [whole, pages, picked],
  );
  const sections = useMemo(() => (work && toc && sectionUids.length ? sectionReadings(work, toc, sectionUids) : []), [work, toc, sectionUids]);

  const total = chapterCount(books);
  const units = Math.max(total, sections.length);
  const dayCount = pace === "days" || total === 0 ? Math.min(days, Math.max(units, 1)) : Math.max(1, Math.ceil(total / perDay));
  const schedule = useMemo(() => (books.length || sections.length ? buildSchedule(books, dayCount, sections) : []), [books, dayCount, sections]);
  const canMake = books.length > 0 || sections.length > 0;
  const finish = schedule.length ? finishDate(localDate(), { days: schedule.length, restSundays }) : null;
  const toggle = (id: string) => setBooks(books.includes(id) ? books.filter((b) => b !== id) : [...books, id]);
  const bookName = books.length === 1 ? CHAPTERS.find(([id]) => id === books[0])![1] : null;
  const suggestedTitle =
    bookName && work ? `${bookName} with ${work.shortTitle}` : bookName ? `${bookName} in ${schedule.length} days` : !books.length && work ? `${work.shortTitle} in ${schedule.length} days` : "";
  const shownTitle = title.trim() || suggestedTitle || "Your plan";

  return (
    <div className={s.page}>
      <div className={ui.pageHead}>
        <IconButton label="Close" icon="close" className={s.close} onClick={() => router.history.back()} />
        <h1 className={s.heading}>Make a plan</h1>
      </div>

      <div className={s.layout}>
        <form
          className={s.form}
          onSubmit={(e) => {
            e.preventDefault();
            if (!canMake) return;
            create.mutate(
              {
                title: title.trim() || suggestedTitle || "My reading plan",
                description,
                books,
                days: schedule.length,
                restSundays,
                visibility: shared ? "friends" : "private",
                ...(work && sectionUids.length ? { alongside: { libraryId: work.id, sectionUids } } : {}),
              },
              { onSuccess: (p) => void navigate({ to: "/plans/about/$planId", params: { planId: p.id } }) },
            );
          }}
        >
          <fieldset className={s.fieldset}>
            <legend className={s.legend}>What will you read?</legend>
            <div className={s.quick}>
              {QUICK.map((q) => (
                <Chip key={q.label} selected={same(books, q.books)} onClick={() => setBooks(q.books)}>
                  {q.label}
                </Chip>
              ))}
              {books.length > 0 && (
                <Button variant="text" size="sm" onClick={() => setBooks([])}>
                  Clear
                </Button>
              )}
            </div>
            {(["Old Testament", "New Testament"] as const).map((t, ti) => (
              <div key={t} className={s.testament}>
                <p className={s.testamentName}>{t}</p>
                <ul className={s.books}>
                  {CHAPTERS.slice(ti === 0 ? 0 : 39, ti === 0 ? 39 : 66).map(([id, name, n]) => (
                    <li key={id}>
                      <button
                        type="button"
                        className={books.includes(id) ? `${s.book} ${s.bookOn}` : s.book}
                        aria-pressed={books.includes(id)}
                        aria-label={`${name}, ${n} chapters`}
                        title={`${name}, ${n} chapters`}
                        onClick={() => toggle(id)}
                      >
                        {id.replace(/^(\d)/, "$1 ")}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <p className={s.count} aria-live="polite">
              {books.length ? `${books.length} ${books.length === 1 ? "book" : "books"}, ${total} chapters.` : "Choose books, or start from a group above. Or read only a book from the library, below."}
            </p>
          </fieldset>

          <fieldset className={s.fieldset}>
            <legend className={s.legend}>
              Read a book alongside <span className={s.optional}>optional</span>
            </legend>
            <Field label="From the library" id="plan-work" hint="Books you can read here. Their sections are spread over the same days, after the Scripture.">
              {(p) => (
                <Select
                  {...p}
                  value={workId}
                  onChange={(e) => {
                    setWorkId(e.target.value);
                    setWhole("whole");
                    setPicked([]);
                  }}
                >
                  <option value="">No book</option>
                  {readable.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.title}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            {work && (
              <>
                <SegmentedControl
                  label="How much of the book"
                  options={[
                    { value: "whole", label: `The whole book, ${pages.length} sections` },
                    { value: "some", label: "Choose sections" },
                  ]}
                  value={whole}
                  onChange={setWhole}
                />
                {whole === "some" && (
                  <>
                    <fieldset className={s.sections}>
                      <legend className="visually-hidden">Sections of {work.title}</legend>
                      {pages.map((p) => (
                        <label key={p.uid} className={s.sectionPick}>
                          <input type="checkbox" checked={picked.includes(p.uid)} onChange={(e) => setPicked(e.target.checked ? [...picked, p.uid] : picked.filter((x) => x !== p.uid))} />
                          <span>{p.label}</span>
                        </label>
                      ))}
                    </fieldset>
                    <p className={ui.hint}>{picked.length ? `${picked.length} chosen.` : "Choose at least one section."}</p>
                  </>
                )}
              </>
            )}
          </fieldset>

          <fieldset className={s.fieldset}>
            <legend className={s.legend}>At what pace?</legend>
            <SegmentedControl
              label="Pace"
              options={[
                { value: "days", label: "Finish in a number of days" },
                { value: "chapters", label: "Chapters a day" },
              ]}
              value={pace}
              onChange={setPace}
            />
            {pace === "days" ? (
              <label className={s.range}>
                <span className={s.rangeValue}>
                  <strong>{days}</strong> days
                </span>
                <input type="range" min={3} max={365} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Number of days" />
              </label>
            ) : (
              <label className={s.range}>
                <span className={s.rangeValue}>
                  <strong>{perDay}</strong> {perDay === 1 ? "chapter" : "chapters"} a day
                </span>
                <input type="range" min={1} max={10} value={perDay} onChange={(e) => setPerDay(Number(e.target.value))} aria-label="Chapters a day" />
              </label>
            )}
            <label className={ui.toggleRow}>
              <span>
                <strong>Rest on the Lord’s Day</strong>
                <br />
                <span className={ui.hint}>No reading set on Sundays; the plan’s dates skip them.</span>
              </span>
              <input type="checkbox" role="switch" className={ui.switch} checked={restSundays} onChange={(e) => setRestSundays(e.target.checked)} />
            </label>
          </fieldset>

          <fieldset className={s.fieldset}>
            <legend className={s.legend}>Name it</legend>
            <Field label="Title" id="plan-title">
              {(p) => <Input {...p} maxLength={80} placeholder={suggestedTitle || "Romans before Easter"} value={title} onChange={(e) => setTitle(e.target.value)} />}
            </Field>
            <Field
              label={
                <>
                  A line about it <span className={s.optional}>optional</span>
                </>
              }
              id="plan-desc"
            >
              {(p) => <Textarea {...p} rows={3} maxLength={400} value={description} onChange={(e) => setDescription(e.target.value)} />}
            </Field>
            <label className={ui.toggleRow}>
              <span>
                <strong>Friends can start it too</strong>
                <br />
                <span className={ui.hint}>Your friends see it among their plans. Off keeps it for you and the people you read it with.</span>
              </span>
              <input type="checkbox" role="switch" className={ui.switch} checked={shared} onChange={(e) => setShared(e.target.checked)} />
            </label>
          </fieldset>

          {create.isError && (
            <p className={ui.error} role="alert">
              {create.error.message}
            </p>
          )}
          <div className={s.submit}>
            <Button type="submit" variant="primary" size="lg" busy={create.isPending} disabled={!canMake || create.isPending}>
              {create.isPending ? "Making…" : "Make the plan"}
            </Button>
          </div>
        </form>

        <aside className={s.aside} aria-label="The plan so far" aria-live="polite">
          <Card pad="md" className={s.preview}>
            <div className={s.previewHead}>
              <PlanCover plan={{ id: "your-plan", title: shownTitle, days: schedule.length || undefined, books }} width={84} board />
              <div className={s.previewWords}>
                <p className={s.previewTitle}>{shownTitle}</p>
                {schedule.length > 0 && (
                  <p className={s.previewMeta}>
                    {schedule.length} days{restSundays ? ", resting on Sundays" : ""}. Started today, you’d finish on {long(finish!)}.
                  </p>
                )}
              </div>
            </div>
            {schedule.length ? (
              <ol className={s.days}>
                {schedule.slice(0, 8).map((d) => (
                  <li key={d.key}>
                    <span className={s.dayN}>{d.heading}</span>
                    <span className={s.dayR}>{readingsLine(d)}</span>
                  </li>
                ))}
                {schedule.length > 8 && <li className={s.more}>and {schedule.length - 8} more days</li>}
              </ol>
            ) : (
              <p className={s.previewEmpty}>Choose what to read, and the days will lay themselves out here.</p>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}
